import type { QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { updateProfileUiFlags } from '@/api/profiles';
import { MY_PROFILE_QUERY_KEY } from '@/constants';
import { trackUmamiEvent } from '@/helpers/analytics';
import type { ProfileDto } from '@/types/profiles';

const UNIQUE_VOTED_DUELS_KEY = 'crowdfpl_unique_voted_duel_ids';

interface FollowXModalStore {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const useFollowXModalStore = create<FollowXModalStore>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

function getUniqueVotedDuelIds(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(UNIQUE_VOTED_DUELS_KEY);
    const arr = raw ? (JSON.parse(raw) as string[]) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveUniqueVotedDuelIds(ids: Set<string>) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(UNIQUE_VOTED_DUELS_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

/**
 * Call after successful vote. If user has 5+ unique votes and hasn't seen the modal,
 * shows the Follow X modal and sets ui_flags on backend.
 */
export function recordVotedDuelAndMaybeShowModal(
  duelId: string,
  selectedPlayerId: string | null,
  queryClient: QueryClient,
): void {
  if (selectedPlayerId === null) return;

  const ids = getUniqueVotedDuelIds();
  ids.add(duelId);
  saveUniqueVotedDuelIds(ids);

  if (ids.size < 5) return;

  const profile = queryClient.getQueryData<ProfileDto>(MY_PROFILE_QUERY_KEY);
  if (profile?.ui_flags?.follow_x_modal_shown === true) return;

  useFollowXModalStore.getState().setOpen(true);
  trackUmamiEvent('follow_x_modal_shown', { uniqueVotesCount: ids.size });

  updateProfileUiFlags({ flag: 'follow_x_modal_shown' })
    .then((res) => {
      queryClient.setQueryData(MY_PROFILE_QUERY_KEY, res.data);
    })
    .catch(() => {
      // Fire-and-forget; user will see modal again on next vote until backend succeeds
    });
}
